
import React, { forwardRef, useEffect, useState, useImperativeHandle, useMemo, useRef } from 'react';
import { Alert, Spin } from 'antd';
import CelebrationService from './celebrationService';
import HatchChooser from './CelebrationChooser';
import './hatchStyle.less';

const GOLD_COMPANY_ID = '00000000-0000-0000-0000-000000000000';

const CelebrationHatch = forwardRef( ( props, ref ) => {
    const { celebrationConfigId } = props;

    // Ref to the <celebrationChooser>
    const celebrationChooser = useRef( null );

    // Auth
    const [ authError, setAuthError ] = useState( false );

    // Celebration Config
    const [ celebrationConfigLoading, setCelebrationConfigLoading ] = useState( false );
    const [ celebrationConfigError, setCelebrationConfigError ] = useState( false );
    const [ celebrationConfigSaveError, setCelebrationConfigSaveError ] = useState( false );

    //Celebration Type
    const [ celebrationTypeData, setCelebrationTypeData ] = useState( [] );
    const [ celebrationTypesLoading, setCelebrationTypesLoading ] = useState( true );
    const [ celebrationTypesError, setCelebrationTypesError ] = useState( false );


    const [ celebrationSelectedKeys, setCelebrationSelectedKeys ] = useState( [] );

    /*************************
       * Ref for save function
    *************************/

    useImperativeHandle( ref, () => ( {
        saveCelebration() {
            if ( !hasError ) {
                console.log( 'Celebration Config Hatch save was clicked' );
                return saveCelebrationConfig();
            }
        }
    } ) );

    /*************************
    *  Auth token handling
    *************************/

    const authToken = useMemo( () => {
        const token = props?.user?.access_token || '';

        if ( token === '' ) {
            setAuthError( true );
            return '';
        } else {
            return token;
        }
    }, [ props.user?.access_token ] );

    /*************************
    *  Celebration Service
    *************************/
    const services = useMemo( () => new CelebrationService( props.user ), [ authToken ] );

    /*************************
    *  Fetch Celebration Types
     *************************/

    const searchCelebrationType = async () => {
        const typeData = [];

        try {
            const response = await services.searchCelebrationType( {
                companyId: GOLD_COMPANY_ID,
                status: 'active'
            } );

            if ( response?.success == true ) {
                const data = response.data.celebrationTypes;
                for ( let i = 0; i < data.length; i++ ) {
                    const result = {
                        key: data[ i ].id,
                        title: data[ i ].label
                    };
                    typeData.push( result );
                }

                setCelebrationTypeData( typeData );
                setCelebrationTypesError( false );
            } else {
                setCelebrationTypesError( true );
            }
        } catch ( error ) {
            console.error( error );
            setCelebrationTypesError( true );

        } finally {
            setCelebrationTypesLoading( false );
        }
    };


    useEffect( () => {
        // prevent setting state on unmounted component when config is deleted
        let unmounted = false;

        if ( authToken && !unmounted ) {
            searchCelebrationType();
        }
        return () => {
            unmounted = true;
        };

    }, [ authToken ] );

    /*************************
    *  Fetch Celebration Config
     *************************/

    const getCelebrationConfig = async ( celebrationConfigId ) => {
        try {
            setCelebrationConfigLoading( true );

            const response = await services.getCelebrationConfigById( celebrationConfigId );

            if ( response.success ) {
                const { data: { celebrationConfig } } = response;
                const selectedTypes = celebrationConfig.types.map( type => type.id );

                setCelebrationSelectedKeys( selectedTypes );
                setCelebrationConfigError( false );

            } else {
                setCelebrationConfigError( true );
            }
        } catch ( error ) {
            console.error( error );

            setCelebrationConfigError( true );

        } finally {
            setCelebrationConfigLoading( false );
        }
    };

    useEffect( () => {
        // prevent setting state on unmounted component when config is deleted
        let unmounted = false;
        if ( authToken && !celebrationTypesError && celebrationConfigId && !unmounted ) {
            getCelebrationConfig( celebrationConfigId );
        }
        return () => {
            unmounted = true;
        };

    }, [ authToken, celebrationTypesError, celebrationConfigId ] );

    /*************************
    *  Save Celebration Config
     *************************/
    const saveCelebrationConfig = async () => {
        // need to get key
        const keys = celebrationChooser.current.getSelectedKeys();

        if ( keys.length === 0 ) {
            return {
                success: true,
                celebrationConfigId: ''
            };
        }
        try {
            if ( celebrationConfigId ) {
                const response = await services.editCelebrationConfig( celebrationConfigId, keys );

                if ( response?.success === true && response?.data?.celebrationConfig.id ) {
                    return {
                        success: true,
                        celebrationConfigId: response.data.celebrationConfig.id
                    };
                } else {
                    setCelebrationConfigSaveError( true );

                    return {
                        success: false,
                        ...response
                    };
                }

            } else {
                // Since celebrationConfigId doesn't exist we create one
                const response = await services.createCelebrationConfig( keys );

                if ( response?.success === true && response?.data?.celebrationConfig.id ) {
                    // setCelebrationConfigId( response.data.celebrationConfig.id );

                    return {
                        success: true,
                        celebrationConfigId: response.data.celebrationConfig.id
                    };
                } else {
                    setCelebrationConfigSaveError( true );

                    return {
                        success: false,
                        ...response
                    };
                }
            }
        } catch ( error ) {
            console.log( error );
            setCelebrationConfigSaveError( true );

            return {
                success: false,
            };
        }

    };
    /**************************
    *  Global is loading state
    **************************/

    const isLoading = useMemo( () => {
        return celebrationConfigLoading || celebrationTypesLoading;
    }, [ celebrationConfigLoading, celebrationTypesLoading ] );
    /**************************
     *  Global is error state
     **************************/

    const hasError = useMemo( () => {
        return authError || celebrationConfigError || celebrationConfigSaveError || celebrationTypesError;
    }, [ authError, celebrationConfigError, celebrationConfigSaveError,
        celebrationConfigError, celebrationTypesError ] );
    /**************************
     *  Global is loading state
     **************************/
    if ( isLoading ) {
        return (
            <div className="celebration-config-hatch">
                <div className="spinner">
                    <Spin size="large" />
                </div>
            </div>
        );
    }
    /*************************
     *  Error state JSX
     *************************/

    if ( hasError ) {
        return (
            <div className="celebration-config-hatch">

                { authError &&
                    <Alert
                        message="Missing or incorrect authentication token."
                        type="error"
                    />
                }

                { celebrationConfigError &&
                    <Alert
                        message="Error fetching celebration config."
                        type="error"
                    />
                }

                { celebrationTypesError &&
                    <Alert
                        message="Error fetching celebration types."
                        type="error"
                    />
                }

            </div>
        );
    }

    return (
        <div className="celebration-config-hatch">
            {
                celebrationConfigSaveError &&
                <Alert
                    message="Error saving celebration."
                    type="error"
                />
            }
            <HatchChooser
                ref={ celebrationChooser }
                celebrationData={ celebrationTypeData }
                celebrationSelectedKeys={ celebrationSelectedKeys }
                deleteCelebrationStack={ props.deleteCelebrationStack }
                celebrationConfigId={ celebrationConfigId }
                services={ services }
            />
        </div>
    );

} );
export default CelebrationHatch;
